import APITemplate from '@api/APITemplate'
import con from '@services/connection'
import store from '@state/store'
import { resolveConsolidationScope } from '@modules/consolidator/utils/consolidation-scope/consolidation-scope'
import { MINUTES_TIMEOUT_REQUESTS } from '@constants/Numbers'
import { formatQueryParams, safeJsonParse } from '@utils/Format'
import { shouldUseCachedData } from '@modules/consolidator/utils/Api/Api'
import {
  adaptAssetSeriesResponse,
  formatarEixo,
  serieDoBenchmark,
} from '@modules/consolidator/utils/adapt-asset-series-response/adapt-asset-series-response'
import { serializeWalletAnalysisParams } from '@modules/consolidator/utils/serialize-wallet-analysis-params/serialize-wallet-analysis-params'

const GROUP_BY = 'id_dado_ativo'

const DATA_REQUESTED = ['PL', 'rentabilidade', 'benchmarks', 'statistics']

const PREFETCH = '__prefetch__'

class APIConsolidatorAssetSeries extends APITemplate {
  constructor() {
    super()

    this.requisicoesEmVoo = {}
    this.janelasCarregadas = {}
    this.clienteEmFoco = null
  }

  /**
   * @param {Object} params
   * @param {Boolean} params.isClient Contexto do cliente final (id vem da sessao)
   * @param {String|Number} params.clienteId id_cliente_app (obrigatorio se profissional)
   * @param {String|Number} params.idAtivo id_dado_ativo do ativo
   * @param {String} params.beginDate Inicio da janela. DD-MM-YYYY
   * @param {String} params.endDate Fim da janela. DD-MM-YYYY
   * @param {Boolean} params.forceUpdate Ignora o cache e refaz a requisicao
   * @returns {Promise<Object>} { status, message, value } — contrato do APITemplate
   */
  carregar({
    isClient,
    clienteId,
    idAtivo,
    beginDate,
    endDate,
    forceUpdate,
  } = {}) {
    if (!idAtivo) {
      return Promise.resolve(
        this.buildErrorResponse({
          status: 400,
          message: 'Não foi possível identificar o ativo.',
          value: null,
        })
      )
    }

    const escopo = resolveConsolidationScope({
      isClient,
      idClienteApp: clienteId,
    })
    const { error, params } = serializeWalletAnalysisParams({
      id_cliente_app: clienteId,
      is_client: escopo.allowRequestWithoutClient,
      group_by: GROUP_BY,
      data_requested: DATA_REQUESTED,
      start_date: beginDate,
      end_date: endDate,
    })

    if (error) {
      return Promise.resolve(
        this.buildErrorResponse({ status: 400, message: error, value: null })
      )
    }

    const idDeCache = escopo.scopeId
    const janela = this.montarJanela({
      clienteId: idDeCache,
      beginDate,
      endDate,
    })
    const emCache = this.lerSerie({
      isClient,
      clienteId,
      idAtivo,
      beginDate,
      endDate,
      forceUpdate,
    })
    if (emCache) return Promise.resolve(emCache)
    if (this.requisicoesEmVoo[janela]) {
      return this.requisicoesEmVoo[janela].then((response) => {
        const gravado = this.lerSerie({
          isClient,
          clienteId,
          idAtivo,
          beginDate,
          endDate,
        })
        return gravado || adaptAssetSeriesResponse({ response, idAtivo })
      })
    }

    return this.buscar({
      janela,
      isClient,
      clienteId,
      idAtivo,
      beginDate,
      endDate,
      params,
    })
  }

  /**
   * Aquece o cache com a janela padrao do grafico, sem ativo alvo. A resposta e a
   * MESMA para todos os ativos do cliente, entao uma ida antecipada faz o modal
   * abrir com o grafico pronto. Fire-and-forget: quem chama nao espera.
   *
   * @param {Object} params
   * @param {Boolean} params.isClient Contexto do cliente final
   * @param {String|Number} params.clienteId id_cliente_app
   * @returns {Promise<Object>} sempre resolve; falha nao propaga
   */
  prefetch({ isClient, clienteId } = {}) {
    const escopo = resolveConsolidationScope({
      isClient,
      idClienteApp: clienteId,
    })
    const janela = this.montarJanela({ clienteId: escopo.scopeId })
    const fresco = shouldUseCachedData({
      lastUpdate: this.janelasCarregadas[janela],
      timeoutMinutes: MINUTES_TIMEOUT_REQUESTS,
    })
    if (fresco) {
      return Promise.resolve(this.buildSuccessResponse({ value: null }))
    }
    return this.carregar({ isClient, clienteId, idAtivo: PREFETCH }).catch(() =>
      this.buildErrorResponse({ status: 500, value: null })
    )
  }

  /**
   * @param {Object} params
   * @param {String|Number} params.clienteId id_cliente_app
   * @param {String|Number} params.idAtivo id_dado_ativo do ativo
   * @param {String} params.beginDate Inicio da janela. DD-MM-YYYY
   * @param {String} params.endDate Fim da janela. DD-MM-YYYY
   * @param {Boolean} params.forceUpdate Ignora o cache
   * @returns {Object|null} { status, message, value } ou null quando nao ha cache valido
   */
  lerSerie({
    clienteId,
    isClient,
    idAtivo,
    beginDate,
    endDate,
    forceUpdate,
  } = {}) {
    const valido = shouldUseCachedData({
      forceUpdate,
      lastUpdate: store.getters['consolidator/assetSeriesLastUpdate'],
      timeoutMinutes: MINUTES_TIMEOUT_REQUESTS,
    })
    if (!valido) return null
    const escopo = resolveConsolidationScope({
      isClient,
      idClienteApp: clienteId,
    })
    if (!escopo.scopeId && !escopo.allowRequestWithoutClient) return null

    const chave = this.montarChave({
      clienteId: escopo.scopeId,
      idAtivo,
      beginDate,
      endDate,
    })
    return store.getters['consolidator/assetSeries'][chave] || null
  }

  montarJanela({ clienteId, beginDate, endDate }) {
    return [clienteId || 'cliente', beginDate || '', endDate || ''].join(':')
  }

  montarChave({ clienteId, idAtivo, beginDate, endDate }) {
    return [
      clienteId || 'cliente',
      idAtivo,
      beginDate || '',
      endDate || '',
    ].join(':')
  }

  buscar({ janela, isClient, clienteId, idAtivo, beginDate, endDate, params }) {
    const escopo = resolveConsolidationScope({
      isClient,
      idClienteApp: clienteId,
    })
    const baseUrl = escopo.url
    const idDeCache = escopo.scopeId

    const foco = idDeCache || 'cliente'
    this.clienteEmFoco = foco

    this.requisicoesEmVoo[janela] = con
      .get(baseUrl + formatQueryParams(params))
      .then(safeJsonParse)

    return this.requisicoesEmVoo[janela]
      .then((response) => {
        const desatualizado = this.clienteEmFoco !== foco
        if (desatualizado || !this.idsDoBreakdown(response).length) {
          return adaptAssetSeriesResponse({ response, idAtivo })
        }
        this.podarCachesDoCliente(idDeCache)
        this.guardarBenchmarks({
          clienteId: idDeCache,
          janela,
          benchmarks: this.benchmarksDoPayload(response),
        })
        const entradas = this.guardarTodos({
          clienteId: idDeCache,
          beginDate,
          endDate,
          idAtivo,
          response,
        })
        if (!entradas) return adaptAssetSeriesResponse({ response, idAtivo })

        this.janelasCarregadas[janela] = new Date()
        const chave = this.montarChave({
          clienteId: idDeCache,
          idAtivo,
          beginDate,
          endDate,
        })
        return (
          entradas[chave] || adaptAssetSeriesResponse({ response, idAtivo })
        )
      })
      .finally(() => {
        delete this.requisicoesEmVoo[janela]
      })
  }

  guardarTodos({ clienteId, beginDate, endDate, idAtivo, response }) {
    const entradas = this.montarEntradas({
      clienteId,
      beginDate,
      endDate,
      idAtivo,
      response,
    })
    if (!Object.keys(entradas).length) return null

    store.dispatch(
      'consolidator/setAssetSeries',
      Object.freeze({
        ...store.getters['consolidator/assetSeries'],
        ...entradas,
      })
    )
    return entradas
  }

  /**
   * Poda as TRES estruturas alimentadas pela resposta de UMA vez, num gatilho so.
   * Cada uma tinha sua propria condicao de saida antecipada (payload sem
   * benchmark, breakdown vazio), entao um cliente sem posicoes podava uma e
   * deixava as outras com o cliente anterior.
   *
   * @param {String|Number} clienteId id_cliente_app
   */
  podarCachesDoCliente(clienteId) {
    if (this.nadaAPodar(clienteId)) return

    store.dispatch(
      'consolidator/setAssetSeries',
      Object.freeze(
        this.soDoCliente(store.getters['consolidator/assetSeries'], clienteId)
      )
    )
    store.dispatch(
      'consolidator/setAssetSeriesBenchmarks',
      Object.freeze(
        this.soDoCliente(
          store.getters['consolidator/assetSeriesBenchmarks'],
          clienteId
        )
      )
    )
    this.janelasCarregadas = this.soDoCliente(this.janelasCarregadas, clienteId)
  }

  /**
   * Entradas do cliente ATUAL, descartando as dos outros. Cache de cliente
   * anterior nunca e reaproveitado (a tela troca de cliente inteiro), e uma
   * janela de Inicio custa ~745 KB — sem poda a sessao acumula sem teto.
   *
   * As TRES estruturas alimentadas pela resposta (`assetSeries`,
   * `assetSeriesBenchmarks` e `janelasCarregadas`) sao podadas pelo MESMO
   * criterio, de proposito: podar so uma delas deixa serie cacheada sem o
   * benchmark correspondente (a linha some do grafico em silencio) ou o
   * prefetch se declarando feito sobre cache vazio (o modal paga o download
   * que o prefetch existia pra evitar).
   *
   * @param {Object} mapa Cache atual, chaveado por `cliente:...`
   * @param {String|Number} clienteId id_cliente_app
   * @returns {Object} so as chaves do cliente
   */
  nadaAPodar(clienteId) {
    const series = store.getters['consolidator/assetSeries'] || {}
    const benchmarks = store.getters['consolidator/assetSeriesBenchmarks'] || {}
    return (
      Object.keys(this.soDoCliente(series, clienteId)).length ===
        Object.keys(series).length &&
      Object.keys(this.soDoCliente(benchmarks, clienteId)).length ===
        Object.keys(benchmarks).length &&
      Object.keys(this.soDoCliente(this.janelasCarregadas, clienteId))
        .length === Object.keys(this.janelasCarregadas).length
    )
  }

  soDoCliente(mapa, clienteId) {
    const prefixo = `${clienteId || 'cliente'}:`
    const doCliente = {}
    for (const chave of Object.keys(mapa || {})) {
      if (chave.startsWith(prefixo)) doCliente[chave] = mapa[chave]
    }
    return doCliente
  }

  montarEntradas({ clienteId, beginDate, endDate, idAtivo, response }) {
    const ids = this.idsDoBreakdown(response)
    if (!ids.length) return {}

    const datesBR = formatarEixo(this.eixoDoPortfolio(response))
    const entradas = {}
    for (const id of ids) {
      const adaptado = adaptAssetSeriesResponse({
        response,
        idAtivo: id,
        datesBR,
      })
      if (adaptado.status !== 200) continue
      const chave = this.montarChave({
        clienteId,
        idAtivo: id,
        beginDate,
        endDate,
      })
      entradas[chave] = adaptado
    }

    const pedida = this.montarChave({ clienteId, idAtivo, beginDate, endDate })
    if (idAtivo && idAtivo !== PREFETCH && !entradas[pedida]) {
      entradas[pedida] = this.buildSuccessResponse({ value: null })
    }
    return entradas
  }

  /**
   * Serie de um benchmark ESCOLHIDO pelo usuario, recortada e rebaseada na janela
   * do ativo. Nao dispara requisicao: as 19 series ja vieram na mesma resposta.
   *
   * @param {Object} params
   * @param {String|Number} params.clienteId id_cliente_app
   * @param {String} params.beginDate Inicio da janela. DD-MM-YYYY
   * @param {String} params.endDate Fim da janela. DD-MM-YYYY
   * @param {String[]} params.nomes Benchmarks a incluir
   * @param {Number} params.inicio Indice do primeiro dia do ativo
   * @returns {Object} mapa nome -> serie
   */
  seriesDeBenchmark({
    clienteId,
    beginDate,
    endDate,
    nomes,
    inicio,
    tamanho,
  } = {}) {
    const escopo = resolveConsolidationScope({ idClienteApp: clienteId })
    const janela = this.montarJanela({
      clienteId: escopo.scopeId,
      beginDate,
      endDate,
    })
    const guardados = store.getters['consolidator/assetSeriesBenchmarks'] || {}
    return serieDoBenchmark({
      benchmarks: guardados[janela],
      nomes,
      inicio,
      tamanho,
    })
  }

  /**
   * Guarda o mapa BRUTO de benchmarks da janela. Mantem TODAS as janelas do
   * cliente atual (o toggle de periodo vai e volta) e descarta as dos outros:
   * o teto vira `ASSET_CHART_PERIODS.length` janelas, independente de quantos
   * clientes a sessao visitou. Resposta sem benchmark nenhum (falha, payload
   * vazio) nao grava, senao um 500 numa janela apagaria a janela boa.
   *
   * @param {Object} params
   * @param {String|Number} params.clienteId id_cliente_app
   * @param {String} params.janela Chave `cliente:begin:end`
   * @param {Object} params.benchmarks Mapa nome -> serie, como veio do payload
   */
  guardarBenchmarks({ clienteId, janela, benchmarks } = {}) {
    if (!benchmarks || !Object.keys(benchmarks).length) return

    const guardados = store.getters['consolidator/assetSeriesBenchmarks'] || {}
    store.dispatch(
      'consolidator/setAssetSeriesBenchmarks',
      Object.freeze({ ...guardados, [janela]: benchmarks })
    )
  }

  benchmarksDoPayload(response) {
    const value = response && response.value
    const portfolio = value && value.portfolio
    if (!portfolio || !portfolio.data) return {}
    return portfolio.data.benchmarks || {}
  }

  eixoDoPortfolio(response) {
    const value = response && response.value
    const portfolio = value && value.portfolio
    if (!portfolio || !portfolio.data) return []
    return portfolio.data.index || []
  }

  idsDoBreakdown(response) {
    const value = response && response.value
    const breakdown = value && value.breakdown
    if (!breakdown || !breakdown.data) return []
    return Object.keys(breakdown.data)
  }
}

const apiConsolidatorAssetSeries = new APIConsolidatorAssetSeries()
export { apiConsolidatorAssetSeries }
